import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getEmployeeNotes, addEmployeeNote, deleteEmployeeNote } from "@/lib/employeeApi";

export const NotesTab = ({ userId }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => getEmployeeNotes(userId).then(setNotes).catch(() => setNotes([])), [userId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    setBusy(true);
    try { await addEmployeeNote(userId, text); setText(""); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed to add note"); }
    setBusy(false);
  };
  const remove = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try { await deleteEmployeeNote(userId, n.id); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-4" data-testid="user-notes-tab">
      <div className="space-y-2">
        <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a note about this employee…" data-testid="user-note-input" />
        <Button type="button" onClick={add} disabled={busy || !text.trim()} className="bg-teal hover:bg-teal-dark text-white" data-testid="user-note-add-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add note"}</Button>
      </div>
      {notes === null ? <Loader2 className="w-5 h-5 animate-spin text-teal" /> : notes.length === 0 ? <p className="text-sm text-slate-500">No notes yet.</p> : notes.map((n) => (
        <div key={n.id} className="p-3 rounded-lg border border-slate-200" data-testid={`user-note-${n.id}`}>
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span><b className="text-navy">{n.author_name}</b> · {new Date(n.created_at).toLocaleString()}</span>
            {(n.author_id === user?.id || user?.super_admin) && <button type="button" onClick={() => remove(n)} className="hover:text-red-600" data-testid={`user-note-delete-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.text}</p>
        </div>
      ))}
    </div>
  );
};
